import {
  computed,
  type EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  type Provider,
} from '@angular/core';

import { Observable, of } from 'rxjs';

import { APP_CONFIG, AUTH_METHOD, AUTH_TOKEN, type TypedProvider } from '@core/common-legacy';
import {
  type AppContextGetter,
  provideTellentAssistantActorGetter,
  provideTellentAssistantAppContextGetter,
  provideTellentAssistantAuthTokenGetter,
  provideTellentAssistantConfig,
  provideTellentAssistantCurrentUserData,
  provideTellentAssistantResourceIdGetter,
  provideTellentConversationStartersContext,
  type TellentAssistantConfig,
  type TellentAssistantCurrentUserData,
} from '@core/tellent-assistant-contracts';
import {
  provideConversationStartersNgrx,
  provideTellentAssistantCurrentThreadNgrx,
  provideTellentAssistantEngagementsNgrx,
  provideTellentAssistantSuggestionsNgrx,
  provideTellentAssistantThreadsNgrx,
} from '@core/tellent-assistant-logic';
import type { ConversationStartersContext } from '@core/tellent-assistant-types';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { ORG_GUID } from '@core/tellent-types';

import { providePresentChartClientTool } from './client-tools/present-chart.provider';
import {
  providePresentCandidatesClientTool,
  providePresentEvaluationsClientTool,
  providePresentEventsClientTool,
  providePresentJobsClientTool,
  providePresentLinksClientTool,
  providePresentRequisitionsClientTool,
  providePresentSourcesClientTool,
  providePresentTalentPoolsClientTool,
  providePresentTasksClientTool,
  providePresentTeamMembersClientTool,
} from './client-tools/present-list.provider';
import { providePresentTableDataClientTool } from './client-tools/present-table-data.provider';
import { provideTellentAssistant } from './tellent-assistant.providers';

interface TellentAssistantHostAppConfig {
  readonly tellentApiUrl: string;
}

export interface TellentAssistantHostGetters {
  config: TypedProvider<TellentAssistantConfig>;
  authTokenGetter: TypedProvider<() => string | null>;
  resourceIdGetter: TypedProvider<() => string | null>;
  currentUserData?: TypedProvider<TellentAssistantCurrentUserData>;
  actorGetter?: TypedProvider<() => string | null>;
  appContextGetter?: TypedProvider<AppContextGetter>;
  startersContext?: TypedProvider<Observable<ConversationStartersContext | null>>;
}

export function buildTellentAssistantPresenterProviders(): Provider[] {
  return [
    providePresentTableDataClientTool,
    providePresentChartClientTool,
    providePresentCandidatesClientTool,
    providePresentTeamMembersClientTool,
    providePresentJobsClientTool,
    providePresentTalentPoolsClientTool,
    providePresentLinksClientTool,
    providePresentSourcesClientTool,
    providePresentEventsClientTool,
    providePresentEvaluationsClientTool,
    providePresentTasksClientTool,
    providePresentRequisitionsClientTool,
  ];
}

export function buildTellentAssistantHostProviders(
  getters: TellentAssistantHostGetters,
): Array<Provider | EnvironmentProviders> {
  return [
    provideTellentAssistant({
      config: { apiUrl: '', devPlayground: false },
      authTokenGetter: () => null,
    }),
    provideTellentAssistantConfig(getters.config),
    provideTellentAssistantAuthTokenGetter(getters.authTokenGetter),
    provideTellentAssistantResourceIdGetter(getters.resourceIdGetter),
    provideTellentAssistantCurrentUserData(
      getters.currentUserData ?? { useFactory: currentUserDataFromFacade },
    ),
    provideTellentAssistantActorGetter(getters.actorGetter ?? { useValue: () => null }),
    provideTellentAssistantAppContextGetter(getters.appContextGetter ?? { useValue: () => ({}) }),
    provideTellentConversationStartersContext(getters.startersContext ?? { useValue: of(null) }),
    ...buildTellentAssistantPresenterProviders(),
  ];
}

export function buildTellentAssistantHostNgrxProviders(): Array<Provider | EnvironmentProviders> {
  return [
    ...provideTellentAssistantThreadsNgrx,
    ...provideTellentAssistantCurrentThreadNgrx,
    ...provideConversationStartersNgrx,
    ...provideTellentAssistantSuggestionsNgrx,
    ...provideTellentAssistantEngagementsNgrx,
  ];
}

export function provideTellentAssistantHostWith(
  getters: TellentAssistantHostGetters,
): EnvironmentProviders {
  return makeEnvironmentProviders([
    ...buildTellentAssistantHostProviders(getters),
    ...buildTellentAssistantHostNgrxProviders(),
  ]);
}

const desktopHostGetters: TellentAssistantHostGetters = {
  config: {
    useFactory: () =>
      resolveConfig(inject<TellentAssistantHostAppConfig>(APP_CONFIG), inject(ORG_GUID)),
  },
  authTokenGetter: {
    useFactory: () => resolveAuthTokenGetter(inject(AUTH_TOKEN), inject(AUTH_METHOD)),
  },
  resourceIdGetter: {
    useFactory: () => resolveResourceIdGetter(currentUserDataFromFacade()),
  },
  actorGetter: {
    useFactory: () => resolveActorGetter(inject(ORG_GUID), currentUserDataFromFacade()),
  },
};

function currentUserDataFromFacade(): TellentAssistantCurrentUserData {
  const facade = inject(TellentCurrentUserDataFacade);
  return {
    userName: computed(() => facade.currentUser()?.firstName ?? ''),
    userGuid: computed(() => facade.currentUserGuid() ?? null),
    memberGuid: computed(() => facade.organizationMembership()?.guid ?? null),
  };
}

export function provideTellentAssistantHost(): EnvironmentProviders {
  return provideTellentAssistantHostWith(desktopHostGetters);
}

export function buildTellentAssistantHostNonNgrxProviders(): Array<
  Provider | EnvironmentProviders
> {
  return buildTellentAssistantHostProviders(desktopHostGetters);
}

function resolveConfig(
  appConfig: TellentAssistantHostAppConfig,
  orgGuid: string | null,
): TellentAssistantConfig {
  // For local development only
  // return { apiUrl: 'http://127.0.0.1:4111', devPlayground: true };

  return {
    apiUrl: `${appConfig.tellentApiUrl}/intelligence/org/${orgGuid}/assistant/proxy`,
    devPlayground: false,
  };
}

function resolveAuthTokenGetter(token: string, method: string): () => string | null {
  return () => (token ? `${method} ${token}` : null);
}

function resolveResourceIdGetter(
  currentUserData: TellentAssistantCurrentUserData,
): () => string | null {
  return () => currentUserData.userGuid();
}

function resolveActorGetter(
  orgGuid: string | null,
  currentUserData: TellentAssistantCurrentUserData,
): () => string | null {
  return () => {
    const user = currentUserData.userGuid();
    const member = currentUserData.memberGuid();
    if (!orgGuid || !user || !member) return null;
    return JSON.stringify({
      type: 'account',
      data: { org: orgGuid, user, member },
      features: ['ask', 'wiki', 'hr_assistant'],
    });
  };
}
